import React, { useEffect, useMemo, useRef } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import RTL from 'components/rtl';
import { routes } from './routes';
import { createCustomTheme } from './theme';
import useSettings from 'hooks/useSettings';
import NotificationListener from 'components/NotificationListener';
import './i18n';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from './api';
import { setStopRefreshCallback } from './utils/tokenRefreshManager';

export default function App() {
  const { settings } = useSettings();
  const theme = createCustomTheme(settings);
  // createBrowserRouter must not be called on every render: it spins up a new
  // history/router instance each time, which can tear down in-flight navigation
  // (e.g. `window.location.href` assignments during logout) before it resolves.
  // Any change to logged-in state (`/`'s element depends on hasToken()) already
  // takes effect via the hard reload that follows login/logout, so this is safe
  // to build once.
  const router = useMemo(() => createBrowserRouter(routes()), []);
  const refreshIntervalRef = useRef(null);

  useEffect(() => {
    // Handle unhandled promise rejections (e.g., dynamic import failures)
    const handleUnhandledRejection = (event) => {
      const error = event.reason;

      // Check if it's a chunk loading error
      const isChunkLoadError =
        error?.name === 'ChunkLoadError' ||
        error?.message?.includes('Loading chunk') ||
        error?.message?.includes(
          'Failed to fetch dynamically imported module'
        ) ||
        error?.message?.includes('Importing a module script failed');

      if (isChunkLoadError) {
        event.preventDefault(); // Prevent the error from being logged
        console.warn(
          'ChunkLoadError detected in promise rejection. Reloading...'
        );

        toast.loading('Обновление приложения...', {
          duration: 2000
        });

        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledRejection
      );
    };
  }, []);

  useEffect(() => {
    const REFRESH_INTERVAL = 13 * 60 * 1000;

    const refreshToken = async () => {
      try {
        const currentToken =
          localStorage.getItem('token') || sessionStorage.getItem('token');
        if (currentToken) {
          await api.post('api/User/refresh');
        }
      } catch (error) {
        console.error('Proactive token refresh failed:', error);
      }
    };

    const stopRefresh = () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };

    const checkAndStartRefresh = () => {
      const currentToken =
        localStorage.getItem('token') || sessionStorage.getItem('token');
      if (currentToken) {
        refreshIntervalRef.current = setInterval(
          refreshToken,
          REFRESH_INTERVAL
        );
      }
    };

    checkAndStartRefresh();

    const handleStorageChange = (e) => {
      if (e.key === 'token' || e.key === null) {
        stopRefresh();
        checkAndStartRefresh();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    setStopRefreshCallback(stopRefresh);

    return () => {
      stopRefresh();
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <ThemeProvider theme={theme}>
        <RTL>
          <CssBaseline />
          <NotificationListener />
          <RouterProvider router={router} />
        </RTL>
      </ThemeProvider>
    </LocalizationProvider>
  );
}
